import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BotaoDirecao, BotaoFixar, CelulaCategoria, CelulaRecebedor } from './LinhaEditavel';
import { SugestoesDeCategoria } from './SugestoesDeCategoria';
import { ApiError, api, baixarArquivo, type CategoryNode } from '../../lib/api';
import { absMoney, amountClass, dateBR, money, parseAmountInput, today } from '../../lib/format';

/**
 * Lancamentos.
 *
 * A lista mostra transferencia com o nome da conta do outro lado e em tom
 * neutro: ela nao e receita nem despesa, e pintar de verde ou vermelho daria
 * a impressao errada de que o dinheiro entrou ou saiu do bolso.
 */
export function TransactionsPage() {
  const queryClient = useQueryClient();
  /**
   * O filtro inicial vem da URL.
   *
   * E o que faz os cartoes do dashboard levarem a algum lugar util:
   * "Despesas do mes" abre esta tela ja so com as saidas, em vez de abrir
   * tudo e deixar a pessoa filtrar de novo.
   */
  const [parametros] = useSearchParams();
  const [filters, setFilters] = useState({
    accountId: parametros.get('accountId') ?? '',
    // Tambem vem de fora: a leitura do mes manda o texto do lancamento para ca
    // quando alguem clica numa linha para ajusta-la.
    search: parametros.get('search') ?? '',
    from: parametros.get('from') ?? '',
    to: parametros.get('to') ?? '',
    kind: parametros.get('kind') ?? '',
  });
  const [mode, setMode] = useState<'none' | 'entry' | 'transfer'>('none');
  const [error, setError] = useState<string | null>(null);

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => api.accounts() });
  const categories = useQuery({ queryKey: ['categories'], queryFn: () => api.categories() });

  /**
   * Quais recebedores ja sao serie mensal.
   *
   * Serve para a linha dizer "fixo" em vez de oferecer de novo um botao que
   * nao criaria nada: as varias compras do mesmo lugar apareceriam todas
   * convidando ao mesmo clique.
   */
  const recurrences = useQuery({ queryKey: ['recurrences'], queryFn: () => api.recurrences() });

  const fixos = useMemo(() => {
    const marcados = new Set<string>();
    for (const serie of recurrences.data?.recurrences ?? []) {
      if (!serie.active) continue;
      marcados.add(`${serie.kind}:${chaveDoNome(serie.counterpartyMatch ?? serie.description)}`);
    }
    return marcados;
  }, [recurrences.data]);

  /** Os filtros escolhidos, sem paginacao. */
  const filtroEmBusca = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.accountId) params.set('accountId', filters.accountId);
    if (filters.kind) params.set('kind', filters.kind);
    if (filters.search) params.set('search', filters.search);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    return params;
  }, [filters]);

  const query = useMemo(() => {
    const params = new URLSearchParams(filtroEmBusca);
    params.set('limit', '50');
    return params.toString();
  }, [filtroEmBusca]);

  const transactions = useQuery({
    queryKey: ['transactions', query],
    queryFn: () => api.transactions(query),
  });

  const refreshAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const remove = useMutation({
    mutationFn: ({ id, wholeGroup }: { id: string; wholeGroup: boolean }) =>
      api.deleteTransaction(id, wholeGroup),
    onSuccess: refreshAll,
    onError: (caught) => setError(messageFor(caught)),
  });

  /**
   * Desfaz um par que a varredura do Open Finance casou por engano.
   *
   * Sem isto o erro nao tinha volta: transferencia nao pode ser editada, e
   * excluir tambem nao resolveria — a regra 4 impede a linha excluida de
   * voltar na varredura seguinte, e o gasto sumiria para sempre.
   */
  const unlink = useMutation({
    mutationFn: (id: string) => api.unlinkTransfer(id),
    onSuccess: refreshAll,
    onError: (caught) => setError(messageFor(caught)),
  });

  const totals = transactions.data?.totals;

  /**
   * O arquivo leva o que os filtros mostram, e nao a pagina que esta na tela.
   *
   * Quem clica em "Exportar" depois de filtrar o ano quer o ano, nao os 50
   * primeiros. Por isso a paginacao fica de fora da busca que vai no link.
   */
  const [baixando, setBaixando] = useState(false);

  const exportar = async () => {
    setBaixando(true);
    setError(null);
    try {
      await baixarArquivo(`/api/v1/lancamentos.csv?${filtroEmBusca.toString()}`);
    } catch (falha) {
      setError(falha instanceof ApiError ? falha.message : 'Nao consegui gerar o arquivo.');
    } finally {
      setBaixando(false);
    }
  };

  return (
    <div className="main">
      <header className="page-header">
        <div className="between">
          <div>
            <h1>Lancamentos</h1>
            <p>Tudo que entrou e saiu, com a origem sempre rastreavel.</p>
          </div>
          <div className="row">
            <button className="btn" onClick={() => setMode(mode === 'entry' ? 'none' : 'entry')}>
              {mode === 'entry' ? 'Cancelar' : 'Novo lancamento'}
            </button>
            <button
              className="btn secondary"
              onClick={() => setMode(mode === 'transfer' ? 'none' : 'transfer')}
            >
              {mode === 'transfer' ? 'Cancelar' : 'Transferencia'}
            </button>
            <button
              className="btn ghost"
              disabled={baixando}
              onClick={() => void exportar()}
              title="Baixa em CSV o que os filtros abaixo estao mostrando"
            >
              {baixando ? 'Gerando...' : 'Exportar'}
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div className="alert error" role="alert">
          {error}
        </div>
      )}

      {mode === 'entry' && (
        <TransactionForm
          accounts={accounts.data?.accounts ?? []}
          categories={categories.data?.categories ?? []}
          onDone={() => {
            setMode('none');
            setError(null);
            refreshAll();
          }}
          onError={setError}
        />
      )}

      {mode === 'transfer' && (
        <TransferForm
          accounts={accounts.data?.accounts ?? []}
          onDone={() => {
            setMode('none');
            setError(null);
            refreshAll();
          }}
          onError={setError}
        />
      )}

      <section className="card">
        <div className="row" style={{ marginBottom: 'var(--space-4)' }}>
          <input
            placeholder="Buscar na descricao"
            value={filters.search}
            onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            aria-label="Buscar"
            style={{ flex: 2, minWidth: 180 }}
          />
          <select
            value={filters.kind}
            onChange={(event) => setFilters({ ...filters, kind: event.target.value })}
            aria-label="Entradas ou saidas"
          >
            <option value="">Entradas e saidas</option>
            <option value="EXPENSE">So saidas</option>
            <option value="INCOME">So entradas</option>
          </select>
          <select
            value={filters.accountId}
            onChange={(event) => setFilters({ ...filters, accountId: event.target.value })}
            aria-label="Conta"
          >
            <option value="">Todas as contas</option>
            {accounts.data?.accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={filters.from}
            onChange={(event) => setFilters({ ...filters, from: event.target.value })}
            aria-label="Data inicial"
          />
          <input
            type="date"
            value={filters.to}
            onChange={(event) => setFilters({ ...filters, to: event.target.value })}
            aria-label="Data final"
          />
        </div>

        {totals && (
          <div className="row" style={{ marginBottom: 'var(--space-4)' }}>
            <span className="muted">{totals.count} lancamento(s)</span>
            <span className="income money">+{money(totals.incomeCents)}</span>
            <span className="expense money">-{money(totals.expenseCents)}</span>
            <span className="money">
              Saldo do periodo: <strong>{money(totals.netCents)}</strong>
            </span>
          </div>
        )}

        {transactions.isLoading ? (
          <p className="muted">Carregando...</p>
        ) : (transactions.data?.transactions.length ?? 0) === 0 ? (
          <p className="muted">Nenhum lancamento neste filtro.</p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Descricao</th>
                  <th>Para quem</th>
                  <th>Categoria</th>
                  <th>Conta</th>
                  <th style={{ textAlign: 'right' }}>Valor</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {transactions.data?.transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="tabular">{dateBR(transaction.date)}</td>
                    <td>
                      {/* O nome legivel em cima, o texto cru do banco
                          embaixo. Conferir com a fatura exige o segundo; usar
                          o sistema todo dia exige o primeiro. */}
                      <span className="payee-name">
                        <span>{transaction.displayName}</span>
                        {transaction.displayName !== transaction.description && (
                          <span className="payee-meta" title="Como veio no extrato">
                            {transaction.description}
                          </span>
                        )}
                      </span>
                      {transaction.kind === 'TRANSFER' &&
                        transaction.transferCounterpartAccountName && (
                          <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                            {BigInt(transaction.amountCents) < 0n ? 'para ' : 'de '}
                            {transaction.transferCounterpartAccountName}
                          </div>
                        )}
                      {transaction.tags.length > 0 && (
                        <div className="row" style={{ gap: 4, marginTop: 4 }}>
                          {transaction.tags.map((tag) => (
                            <span key={tag.id} className="badge">
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    {/* Clicaveis: corrigir quem recebeu ou a categoria e
                        coisa de um segundo, e abrir um formulario para isso
                        faria ninguem corrigir nada. */}
                    <td>
                      <CelulaRecebedor transaction={transaction} />
                    </td>
                    <td>
                      <CelulaCategoria
                        transaction={transaction}
                        categorias={categories.data?.categories ?? []}
                      />
                    </td>
                    <td className="muted">{transaction.accountName}</td>
                    <td
                      className={`money ${amountClass(transaction.amountCents)}`}
                      style={{ textAlign: 'right' }}
                    >
                      {BigInt(transaction.amountCents) < 0n ? '-' : '+'}
                      {absMoney(transaction.amountCents)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <BotaoDirecao transaction={transaction} />
                      <BotaoFixar
                        transaction={transaction}
                        jaEhFixo={fixos.has(
                          `${transaction.kind}:${chaveDoNome(
                            transaction.counterpartyName ?? transaction.description,
                          )}`,
                        )}
                      />
                      {transaction.kind === 'TRANSFER' &&
                        transaction.source === 'OPEN_FINANCE' &&
                        transaction.paidInvoiceId === null && (
                          <button
                            className="btn ghost"
                            onClick={() => unlink.mutate(transaction.id)}
                            title="Nao era transferencia: separar de volta em despesa e receita."
                          >
                            Nao e transferencia
                          </button>
                        )}
                      <button
                        className="btn ghost"
                        onClick={() =>
                          remove.mutate({
                            id: transaction.id,
                            wholeGroup: transaction.purchaseGroupId !== null,
                          })
                        }
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <SugestoesDeCategoria />
    </div>
  );
}

// -------------------------------------------------------------------- forms

function TransactionForm({
  accounts,
  categories,
  onDone,
  onError,
}: {
  accounts: Array<{ id: string; name: string; type: string; archived: boolean }>;
  categories: CategoryNode[];
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const active = accounts.filter((account) => !account.archived);

  /**
   * A conta escolhida e DERIVADA, nao guardada de saida.
   *
   * Guardar `active[0]` no estado inicial so funciona se a lista ja tiver
   * chegado quando o formulario monta. Quem abre o formulario antes disso
   * ficava com a escolha vazia para sempre: o navegador mostrava a primeira
   * conta no seletor, mas o React continuava achando que nao havia nenhuma, e
   * o botao de lancar ficava desligado sem nada explicando por que.
   *
   * Derivar resolve sem efeito colateral: enquanto a pessoa nao escolher — ou
   * se a conta escolhida sumir da lista, por arquivamento — vale a primeira.
   */
  const [chosenAccountId, setChosenAccountId] = useState('');
  const accountId = active.some((account) => account.id === chosenAccountId)
    ? chosenAccountId
    : (active[0]?.id ?? '');
  const [kind, setKind] = useState<'EXPENSE' | 'INCOME'>('EXPENSE');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(today());
  const [categoryId, setCategoryId] = useState('');
  const [installments, setInstallments] = useState('1');

  const selected = active.find((account) => account.id === accountId);
  const isCard = selected?.type === 'CREDIT_CARD';

  const options = useMemo(() => flattenCategories(categories, kind), [categories, kind]);

  const create = useMutation({
    mutationFn: () =>
      api.createTransaction({
        accountId,
        date,
        kind,
        amountCents: parseAmountInput(amount).toString(),
        description,
        ...(categoryId ? { categoryId } : {}),
        ...(isCard && kind === 'EXPENSE' ? { installments: Number(installments) } : {}),
      }),
    onSuccess: onDone,
    onError: (caught) => onError(messageFor(caught)),
  });

  let amountIsValid = false;
  try {
    amountIsValid = amount.trim() !== '' && parseAmountInput(amount) > 0n;
  } catch {
    amountIsValid = false;
  }

  return (
    <section className="card">
      <h2 className="card-title">Novo lancamento</h2>

      <div className="row">
        <div className="field" style={{ flex: 1, minWidth: 150 }}>
          <label htmlFor="kind">Tipo</label>
          <select
            id="kind"
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as 'EXPENSE' | 'INCOME');
              setCategoryId('');
            }}
          >
            <option value="EXPENSE">Despesa</option>
            <option value="INCOME">Receita</option>
          </select>
        </div>

        <div className="field" style={{ flex: 1, minWidth: 150 }}>
          <label htmlFor="amount">Valor</label>
          <input
            id="amount"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="0,00"
            autoFocus
          />
        </div>

        <div className="field" style={{ flex: 1, minWidth: 150 }}>
          <label htmlFor="date">Data</label>
          <input
            id="date"
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="description">Descricao</label>
        <input
          id="description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Mercado, honorarios, farmacia..."
        />
      </div>

      <div className="row">
        <div className="field" style={{ flex: 1, minWidth: 180 }}>
          <label htmlFor="account">Conta</label>
          <select
            id="account"
            value={accountId}
            onChange={(event) => setChosenAccountId(event.target.value)}
          >
            {active.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field" style={{ flex: 1, minWidth: 180 }}>
          <label htmlFor="category">Categoria</label>
          <select
            id="category"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
          >
            <option value="">Sem categoria</option>
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {isCard && kind === 'EXPENSE' && (
          <div className="field" style={{ flex: 1, minWidth: 120 }}>
            <label htmlFor="installments">Parcelas</label>
            <input
              id="installments"
              inputMode="numeric"
              value={installments}
              onChange={(event) => setInstallments(event.target.value)}
            />
            <span className="field-hint">Uma parcela por fatura.</span>
          </div>
        )}
      </div>

      <button
        className="btn"
        onClick={() => create.mutate()}
        disabled={!accountId || !amountIsValid || description.trim().length < 1 || create.isPending}
      >
        {create.isPending ? 'Lancando...' : 'Lancar'}
      </button>
    </section>
  );
}

function TransferForm({
  accounts,
  onDone,
  onError,
}: {
  accounts: Array<{ id: string; name: string; archived: boolean }>;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const active = accounts.filter((account) => !account.archived);
  // Derivadas pelo mesmo motivo do formulario de lancamento: o formulario
  // pode abrir antes de a lista de contas chegar.
  const [chosenFrom, setChosenFrom] = useState('');
  const [chosenTo, setChosenTo] = useState('');
  const fromAccountId = active.some((account) => account.id === chosenFrom)
    ? chosenFrom
    : (active[0]?.id ?? '');
  const toAccountId = active.some((account) => account.id === chosenTo)
    ? chosenTo
    : (active.find((account) => account.id !== fromAccountId)?.id ?? '');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today());
  const [description, setDescription] = useState('Transferencia');

  const create = useMutation({
    mutationFn: () =>
      api.createTransfer({
        fromAccountId,
        toAccountId,
        date,
        amountCents: parseAmountInput(amount).toString(),
        description,
      }),
    onSuccess: onDone,
    onError: (caught) => onError(messageFor(caught)),
  });

  return (
    <section className="card">
      <h2 className="card-title">Transferencia entre contas</h2>
      <p className="card-hint">
        Nao conta como receita nem despesa: o dinheiro apenas troca de lugar.
      </p>

      <div className="row">
        <div className="field" style={{ flex: 1, minWidth: 160 }}>
          <label htmlFor="from">De</label>
          <select
            id="from"
            value={fromAccountId}
            onChange={(event) => setChosenFrom(event.target.value)}
          >
            {active.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field" style={{ flex: 1, minWidth: 160 }}>
          <label htmlFor="to">Para</label>
          <select id="to" value={toAccountId} onChange={(event) => setChosenTo(event.target.value)}>
            {active.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field" style={{ flex: 1, minWidth: 140 }}>
          <label htmlFor="transferAmount">Valor</label>
          <input
            id="transferAmount"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="0,00"
          />
        </div>

        <div className="field" style={{ flex: 1, minWidth: 140 }}>
          <label htmlFor="transferDate">Data</label>
          <input
            id="transferDate"
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="transferDescription">Descricao</label>
        <input
          id="transferDescription"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>

      <button
        className="btn"
        onClick={() => create.mutate()}
        disabled={
          !fromAccountId ||
          !toAccountId ||
          fromAccountId === toAccountId ||
          amount.trim() === '' ||
          create.isPending
        }
      >
        {create.isPending ? 'Transferindo...' : 'Transferir'}
      </button>
    </section>
  );
}

function flattenCategories(
  nodes: CategoryNode[],
  kind: 'INCOME' | 'EXPENSE',
): Array<{ id: string; label: string }> {
  const result: Array<{ id: string; label: string }> = [];

  for (const node of nodes) {
    if (node.kind !== kind || node.archived) continue;
    result.push({ id: node.id, label: node.name });
    for (const child of node.children) {
      if (child.archived) continue;
      result.push({ id: child.id, label: `   ${node.name} / ${child.name}` });
    }
  }
  return result;
}

function messageFor(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Nao foi possivel concluir a operacao.';
}

/**
 * O nome, normalizado para comparar.
 *
 * O mesmo recebedor aparece com e sem acento, com caixa diferente e com
 * espaco a mais — e o servidor compara do mesmo jeito na hora de decidir se a
 * serie ja existe. As duas pontas precisam concordar, senao a linha ofereceria
 * um botao que nao cria nada.
 */
function chaveDoNome(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}
